import { useState } from 'react';

import { JobViewHeader } from 'features/JobView/components/JobViewHeader';
import { TabbedJobViewHeaderProps } from './types';
import { useQueryGetHeaderDateByJobId, useToggleJobStatusMutation } from './queries';
import auth from 'utils/auth';
import ShareToSocialMedia from 'components/ShareToSocialMedia';
import { Alert } from 'sharedComponents/Alert';
import {
  DEFAULT_WRITE_ERROR_MESSAGE,
  getAxiosErrorMessageByStatusCode,
} from 'hooks/errorHandling/errorHandlingHooks';
import { HeaderContainer } from './styles';
import { TabbedJobViewHeaderSkeleton } from './TabbedJobViewHeaderSkeleton';
import { DeletionModal } from 'sharedComponents/Modal/DeletionModal';
import { useConvertToDraftMutation } from 'features/ItemsList/mutations';
import { useQueryGetJobById } from 'features/JobRequestUpdate/queries';
import { useHistory } from 'react-router';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { JobRequestModal } from 'features/JobRequestUpdate/JobRequestModal';
import { ATSTabbedJobViewHeaderTestIds } from 'data-testids/ATS';
import { JobRequestModalOpeningOrigin } from 'features/JobRequestUpdate/types';
import { useQueryAdminsInDistrict } from '../InternalNotesTab/queries';
import { roleStatusByValue } from 'utils/typedEnums';

export type TabbedJobViewModalKind = null | 'review' | 'share' | 'error' | 'request';

export const TabbedJobViewHeader: React.FC<TabbedJobViewHeaderProps> = ({ jobId }) => {
  const [currentModal, setCurrentModal] = useState<{ kind: TabbedJobViewModalKind }>({
    kind: null,
  });
  const { data, error, isError, isLoading } = useQueryGetHeaderDateByJobId(jobId);
  const { data: job, isLoading: isLoadingJobData } = useQueryGetJobById(jobId);
  const currentUser = auth.getUser();
  const errorMessage = getAxiosErrorMessageByStatusCode(error);
  const schoolId =
    auth.isSchoolAdmin() && job?.status === roleStatusByValue.submitted
      ? job?.schoolroles?.[0]?.id
      : undefined;
  const jobToShare = { ...data, id: jobId, ...(schoolId ? { schoolId } : {}) };
  const toggleStatusMutation = useToggleJobStatusMutation({});
  const userDistrict = currentUser?.profile.district?.id;
  const history = useHistory();
  const { data: admins } = useQueryAdminsInDistrict();
  const requestor = admins?.find((admin) => admin.id === job?.submitted_by);

  const handleOpenModal = (newKind: TabbedJobViewModalKind) => setCurrentModal({ kind: newKind });
  const handleCloseModal = () => setCurrentModal({ kind: null });

  const handleToggle = () => {
    return toggleStatusMutation.mutate({ jobId, activeStatus: data?.active_status });
  };
  const handleSuccess = () => {
    handleCloseModal();
    history.push('/district/jobslist');
  };

  const { mutate, isLoading: isConvertToDraftLoading } = useConvertToDraftMutation({
    onSuccess: () => {
      handleCloseModal();
      if (job) {
        history.push(`/district/jobedit/${job.id}`);
      }
    },
    onError: () => {
      handleOpenModal('error');
    },
  });

  const handleDeletionClick = () => {
    if (job?.id) {
      mutate(job.id);
    }
  };

  if (isError) {
    return (
      <Alert type="error" data-testid={ATSTabbedJobViewHeaderTestIds.ERROR_MESSAGE}>
        {errorMessage}
      </Alert>
    );
  }

  if (isLoading) {
    return <TabbedJobViewHeaderSkeleton />;
  }

  return (
    <HeaderContainer>
      {data && (
        <>
          <JobViewHeader
            userDistrict={userDistrict}
            isPreview={false}
            isInternalCandidate={false}
            job={jobToShare}
            handleOpenShareModal={() => handleOpenModal('share')}
            toggleJobStatus={handleToggle}
            isPublicJobView={false}
            handleOpenReviewModal={() => handleOpenModal('review')}
            handleOpenRequestModal={() => handleOpenModal('request')}
          />
          {currentModal.kind === 'share' && (
            <ShareToSocialMedia
              isOpen={currentModal.kind === 'share'}
              onClose={handleCloseModal}
              roleToShare={jobToShare}
            />
          )}
          {currentModal.kind === 'request' && (
            <JobRequestModal
              handleSetErrorToast={() => setCurrentModal({ kind: 'error' })}
              isJobRequestModalOpen={currentModal.kind === 'request'}
              onClose={handleSuccess}
              isLoading={isLoadingJobData}
              role={job}
              requester={requestor?.name || 'Unknown User'}
              initialAction={null}
              origin={JobRequestModalOpeningOrigin.TABBED_JOB_VIEW}
            />
          )}
          {currentModal.kind === 'review' && (
            <DeletionModal
              text="This job will be unpublished and hidden from your job board until you repost it. Continue?"
              title="Editing will move this job to Draft"
              open={currentModal.kind === 'review'}
              onClose={handleCloseModal}
              onDelete={handleDeletionClick}
              submitCTAText="Continue"
              disabled={isConvertToDraftLoading}
            />
          )}
          {currentModal.kind === 'error' && (
            <ErrorSnackbar
              open={currentModal.kind === 'error'}
              message={DEFAULT_WRITE_ERROR_MESSAGE}
              onClose={handleCloseModal}
              dataTestId={ATSTabbedJobViewHeaderTestIds.ERROR_MESSAGE}
            />
          )}
        </>
      )}
    </HeaderContainer>
  );
};
